import { useMemo, useState } from 'react'
import { PackageCheck, PackagePlus, Plus } from 'lucide-react'
import { PageHeader } from '@/components/page-header'
import { FilterBar } from '@/components/filter-bar'
import { DataTable } from '@/components/data-table'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { DataLoadError } from '@/components/data-load-error'
import { DataUnavailable } from '@/components/data-unavailable'
import { useProductsData } from '@/hooks/use-products-data'
import type { StudioReviewUnavailableReasonCode } from '@/hooks/dashboard-data-state'
import type { StudioReviewDataClient } from '@/services/dashboard-api'
import { useStudioStore } from '@/store/studio-store'
import { pushStudioRoute } from '@/lib/studio-navigation'
import type { ProductRecord } from '@/types/dashboard'
import { DashboardContainer } from '../components/dashboard-container'
import { OperationsTabs } from '../components/operations-tabs'
import { ReviewObservationDisclosure } from '../components/review-observation-disclosure'
import { useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'

type DataCopy = StudioDictionary['businessScreens']['data']

/** Column headers are words on the screen, so they are built per render from
 *  the active locale rather than frozen at module scope. */
function columnsFor(copy: DataCopy) {
  return [
    { key: 'name' as const, header: copy.columns.name },
    { key: 'app_name' as const, header: copy.columns.artifact },
    { key: 'price' as const, header: copy.columns.confidence, align: 'right' as const, render: (v: unknown) => `${v as number}%` },
    { key: 'stock' as const, header: copy.columns.rows, align: 'right' as const, render: (v: unknown) => v !== null ? String(v) : '—' },
    { key: 'status' as const, header: copy.columns.status, render: (v: unknown) => {
      const status = v as ProductRecord['status']
      return <StatusBadge status={status} label={status === 'active' ? copy.readyStatus : undefined} />
    } },
  ]
}

interface ProductsPageProps {
  dataClient?: StudioReviewDataClient
}

interface ProductsMeasuredProps {
  products: ProductRecord[]
  observedAt: string
  evidenceRefs: string[]
  copy: DataCopy
}

function ProductsMeasured({ products, observedAt, evidenceRefs, copy }: ProductsMeasuredProps) {
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({})
  const filters = useMemo(() => [
    {
      key: 'app',
      label: copy.filters.artifact,
      options: [
        { value: '', label: copy.filters.allArtifacts },
        ...Array.from(new Set(products.map((product) => product.app_name))).map((appName) => ({
          value: appName,
          label: appName,
        })),
      ],
    },
    {
      key: 'status',
      label: copy.filters.status,
      options: [
        { value: '', label: copy.filters.all },
        { value: 'active', label: copy.filters.ready },
        { value: 'draft', label: copy.filters.draft },
        { value: 'archived', label: copy.filters.archived },
      ],
    },
  ], [products, copy])
  const filtered = useMemo(() => products.filter((product) => {
    if (activeFilters.app && product.app_name !== activeFilters.app) return false
    if (activeFilters.status && product.status !== activeFilters.status) return false
    return true
  }), [products, activeFilters])
  const summary = useMemo(() => ({
    total: products.length,
    active: products.filter((product) => product.status === 'active').length,
    draft: products.filter((product) => product.status === 'draft').length,
  }), [products])

  return (
    <>
      <ReviewObservationDisclosure observedAt={observedAt} evidenceRefs={evidenceRefs} scope={copy.scope} />
      <section className="grid gap-3 sm:grid-cols-3" aria-label={copy.summaryLabel}>
        {[
          [copy.tiles.sources, summary.total, PackageCheck],
          [copy.tiles.ready, summary.active, PackagePlus],
          [copy.tiles.drafts, summary.draft, PackageCheck],
        ].map(([label, value, Icon]) => {
          const IconComponent = Icon as typeof PackageCheck
          return (
            <div key={label as string} className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label as string}</span>
                <IconComponent className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-2 text-2xl font-semibold text-foreground">{value as number}</div>
            </div>
          )
        })}
      </section>
      <FilterBar filters={filters} activeFilters={activeFilters} onFilterChange={(key, val) => setActiveFilters((previous) => ({ ...previous, [key]: val }))} />
      <DataTable columns={columnsFor(copy)} data={filtered} />
    </>
  )
}

export function ProductsPage({ dataClient }: ProductsPageProps = {}) {
  const { data: products, status, reviewState, observedAt, evidenceRefs, unavailableReasonCode, unavailableReason, reload } = useProductsData(dataClient)
  const setStudioRoute = useStudioStore((state) => state.setStudioRoute)
  const { strings } = useStudioLocale()
  const copy = strings.businessScreens.data
  const pageState = status === 'ready' ? reviewState ?? 'error' : status

  function goToImport() {
    setStudioRoute('import')
    pushStudioRoute('import')
  }

  let content: React.ReactNode
  switch (pageState) {
    case 'loading':
      content = (
        <div className="animate-pulse space-y-4" role="status">
          <p className="text-sm text-muted-foreground">{copy.loading}</p>
          <div className="h-48 rounded-xl bg-muted" aria-hidden="true" />
        </div>
      )
      break
    case 'measured':
      content = (
        <ProductsMeasured
          products={products as ProductRecord[]}
          observedAt={observedAt as string}
          evidenceRefs={evidenceRefs}
          copy={copy}
        />
      )
      break
    case 'empty':
      content = (
        <div className="rounded-xl border border-dashed border-border bg-card p-6" role="status">
          <p className="text-sm font-medium text-foreground">{copy.empty}</p>
          <ReviewObservationDisclosure observedAt={observedAt as string} evidenceRefs={evidenceRefs} scope={copy.scope} state="empty" />
        </div>
      )
      break
    case 'unavailable': {
      const guidance = copy.unavailable[unavailableReasonCode as StudioReviewUnavailableReasonCode]
      content = (
        <>
          <DataUnavailable summary={guidance.summary} message={unavailableReason as string} testId="products-unavailable" />
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground">{guidance.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{guidance.detail}</p>
          </section>
        </>
      )
      break
    }
    case 'error':
      content = <DataLoadError message={copy.loadFailed} onRetry={reload} testId="products-error" />
      break
  }

  return (
    <DashboardContainer className="py-6 space-y-6">
      <OperationsTabs activeRoute="products" />
      <PageHeader
        route="products"
        title={strings.navigation.data}
        description={copy.description}
        action={(
          <Button size="lg" className="px-3 text-sm" data-testid="products-add-source" onClick={goToImport}>
            <Plus className="h-4 w-4 mr-1" />{copy.addSource}
          </Button>
        )}
      />
      {content}
    </DashboardContainer>
  )
}
